import {
  type AtomCommandResult,
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@afar-research/client-runtime/state/runtime";
import type { MessageId } from "@afar-research/contracts";

import {
  type ComposerDraftOccupancy,
  composerDraftIsUntouched,
  resolveFailedSendDisposition,
} from "./composerDraftOccupancy";
import { commandFailureMessage } from "~/rpc/transportError";

/**
 * Undoing a send that never landed. The optimistic message always goes, and
 * the user's draft comes back when they have not started typing something
 * else.
 */
export interface FailedSendCompensationInput {
  /** The outcome to compensate for; its cause decides message and silence. */
  readonly failure: AtomCommandResult<unknown, unknown>;
  readonly messageId: MessageId;
  /** Whether the send consumed the composer draft on its way out. */
  readonly consumedComposerDraft: boolean;
  /** A Brain Operation carries its own attachments, so they are not a draft. */
  readonly ignorePathAttachments: boolean;
  /** What the composer holds right now, to tell a stale draft from a new one. */
  readonly occupancy: ComposerDraftOccupancy;
}

export interface FailedSendCompensationPorts {
  readonly optimistic: {
    /** Removes the message and revokes any preview URLs it was holding. */
    readonly removeUserMessage: (messageId: MessageId) => void;
  };
  /** Puts the consumed draft back where the user left it. */
  readonly restoreDraft: () => void;
  readonly reportThreadError: (message: string | null) => void;
}

/** What a failed command has to say for itself, or a stated fallback. */
function describeFailure(failure: AtomCommandResult<unknown, unknown>, fallback: string): string {
  if (failure._tag !== "Failure") {
    return fallback;
  }
  const squashed = squashAtomCommandFailure(failure);
  return commandFailureMessage(squashed, fallback);
}

export function compensateFailedSend(
  input: FailedSendCompensationInput,
  ports: FailedSendCompensationPorts,
): void {
  const failureMessage = describeFailure(input.failure, "Failed to send message.");
  ports.optimistic.removeUserMessage(input.messageId);

  const disposition = resolveFailedSendDisposition({
    consumedComposerDraft: input.consumedComposerDraft,
    composerUntouched: composerDraftIsUntouched({
      ignorePathAttachments: input.ignorePathAttachments,
      occupancy: input.occupancy,
    }),
  });

  if (disposition === "restore-draft") {
    ports.restoreDraft();
  }

  // An interruption is the user's own doing; it is not reported back to them.
  if (!isAtomCommandInterrupted(input.failure)) {
    ports.reportThreadError(failureMessage);
  }
}
